// src/data/mock.data.projects.ts
import { cockpitGet, COCKPIT_URL } from '../config/cockpit';
import { FALLBACK_IMAGE } from '../constants/ImagePaths';
import { Project, ProjectsTypes } from '../models/Project';

import { NavigationLink } from './../models/Header';

export const projectsLinks: NavigationLink[] = [
  { id: 0, title: 'about', linkTo: '/about' },
  { id: 1, title: 'expertise', linkTo: '/#expertise' },
  { id: 2, title: 'projects', linkTo: '/projects' },
  { id: 3, title: 'contact', linkTo: '/#contact' },
];

type CockpitAsset = {
  path?: string;
};

function imageUrl(asset?: CockpitAsset): string | undefined {
  if (!asset?.path) return undefined;

  return `${COCKPIT_URL}/storage/uploads${asset.path}`;
}

type CockpitProjectItem = {
  id?: number;
  order?: number;
  title: string;
  shortDescription?: string;
  type?: string;

  heroImage?: CockpitAsset;

  category?: string;
  client?: string;
  locationName?: string;
  year?: string;

  // Cockpit isn't guaranteed to return a string[] here
  // (could be string, object, etc.) so keep it flexible:
  collabs?: unknown;

  conceptImage?: CockpitAsset;
  conceptDescription?: string;

  processImage?: CockpitAsset;
  processDescription?: string;

  images?: CockpitAsset[];
};

function normalizeCollabs(input: unknown): string[] {
  if (Array.isArray(input)) {
    return input
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter((v) => Boolean(v));
  }

  if (typeof input === 'string') {
    // supports "Alice,Bob" or "Alice, Bob"
    return input
      .split(',')
      .map((s) => s.trim())
      .filter((s) => Boolean(s));
  }

  if (input && typeof input === 'object') {
    // supports Cockpit shapes like { entries: [...] } or { items: [...] }
    const obj = input as Record<string, unknown>;
    const entries = obj.entries;
    const items = obj.items;

    if (Array.isArray(entries)) {
      return entries
        .map((v) => (typeof v === 'string' ? v.trim() : ''))
        .filter((v) => Boolean(v));
    }

    if (Array.isArray(items)) {
      return items
        .map((v) => (typeof v === 'string' ? v.trim() : ''))
        .filter((v) => Boolean(v));
    }
  }

  return [];
}

export async function getProjects(): Promise<Project[]> {
  const items: CockpitProjectItem[] = await cockpitGet<CockpitProjectItem[]>(
    '/api/content/items/Projects'
  );

  items.sort(
    (a: CockpitProjectItem, b: CockpitProjectItem) =>
      (a.order ?? 0) - (b.order ?? 0)
  );

  const mapType = (t?: string): ProjectsTypes => {
    switch ((t || '').toLowerCase()) {
      case 'residential':
        return ProjectsTypes.RESIDENTIAL;
      case 'commercial':
        return ProjectsTypes.COMMERCIAL;
      case 'concept':
        return ProjectsTypes.CONCEPT;
      default:
        return ProjectsTypes.RESIDENTIAL;
    }
  };

  return items.map((p: CockpitProjectItem, idx: number) => ({
    id: p.id ?? idx,
    order: p.order ?? 0,
    title: p.title,
    shortDescription: (p.shortDescription || '').replace('&apos;', "'"),
    type: mapType(p.type),
    heroImage: imageUrl(p.heroImage) || FALLBACK_IMAGE,

    category: p.category || '',
    client: p.client || '',
    location: { name: p.locationName || '' },
    year: p.year || '',

    collabs: normalizeCollabs(p.collabs),

    concept:
      p.conceptDescription || p.conceptImage
        ? {
            image: imageUrl(p.conceptImage),
            description: (p.conceptDescription || '').replace('&apos;', "'"),
          }
        : undefined,

    process:
      p.processDescription || p.processImage
        ? {
            image: imageUrl(p.processImage),
            description: (p.processDescription || '').replace('&apos;', "'"),
          }
        : undefined,

    images: (p.images || []).map(
      (a: CockpitAsset) => imageUrl(a) || FALLBACK_IMAGE
    ),
  }));
}